import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  CheckCircle, Phone, Clock, Calendar, Play, 
  ArrowRight, Heart, Star, Users, Shield, Video, MessageCircle
} from 'lucide-react';
import { getSafeSsoParam } from '../utils/ssoUrlSafety';

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_f760df05-0c76-4756-9fc9-778e88d148c4/artifacts/drje6014_Logo.png";

const TrialThankYouPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get customer info from URL params (passed after payment)
  const customerName = searchParams.get('name') || 'there';
  const productName = searchParams.get('product') || 'Trial Class';
  const amountPaid = searchParams.get('amount') || '99';

  // V24 + V28 — read SSO from sessionStorage first (head-script stashed it
  // there before any tracker fired), URL fallback for backward compat.
  const ssoUrl = getSafeSsoParam(searchParams, 'sso');
  const [ssoCountdown, setSsoCountdown] = useState(8);

  useEffect(() => {
    if (!ssoUrl) return;
    const t = setInterval(() => {
      setSsoCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(t);
          window.location.href = ssoUrl;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [ssoUrl]);

  // Placeholder video URL - replace with actual video from Aanchal
  const VIDEO_URL = searchParams.get('video') || '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
      {/* Compact Header with Logo */}
      <header className="pt-2 md:pt-4">
        <div className="flex justify-center -mb-2 md:-mb-4">
          {/* Logo with negative margins to counteract built-in whitespace */}
          <img 
            src={LOGO_URL} 
            alt="Soul Sanskar" 
            className="h-20 md:h-32 w-auto -my-3 md:-my-6"
          />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-2 md:py-6">
        {/* Success Message */}
        <div className="text-center mb-6 animate-fade-in">
          <div className="w-16 h-16 md:w-20 md:h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <CheckCircle className="w-10 h-10 md:w-12 md:h-12 text-green-500" />
          </div>
          <h1 className="text-xl md:text-3xl font-bold text-slate-800 mb-2">
            Thank You, {customerName.split(' ')[0]}! 🎉
          </h1>
          <p className="text-base md:text-lg text-slate-600">
            Your registration for <span className="font-semibold text-green-600">{productName}</span> is confirmed!
          </p>
          <p className="text-sm text-slate-500 mt-1">
            Payment of ₹{amountPaid} received successfully
          </p>

          {/* V24 — Auto-login banner */}
          {ssoUrl && (
            <div
              className="mt-5 inline-flex flex-col items-center gap-2 bg-white border border-indigo-200 rounded-xl shadow-sm px-5 py-3"
              data-testid="trial-thank-you-sso-banner"
            >
              <p className="text-sm text-slate-600">
                Logging you into your dashboard in{' '}
                <span className="font-bold text-indigo-600">{ssoCountdown}</span>{' '}
                seconds…
              </p>
              <a
                href={ssoUrl}
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700"
                data-testid="trial-thank-you-go-now-btn"
              >
                Go to my dashboard now <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          )}
        </div>

        {/* Video Section */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8" data-testid="video-section">
          <div className="bg-gradient-to-r from-rose-500 to-orange-500 p-4">
            <h2 className="text-white text-lg md:text-xl font-semibold text-center flex items-center justify-center gap-2">
              <Play className="w-5 h-5" />
              A Special Message from Aanchal Ma'am
            </h2>
          </div>
          
          <div className="relative aspect-video bg-slate-900">
            {VIDEO_URL ? (
              <iframe
                src={VIDEO_URL}
                title="Welcome Message from Aanchal"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900 p-8">
                <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mb-4 animate-pulse">
                  <Play className="w-10 h-10 text-white" />
                </div>
                <p className="text-white/80 text-center text-lg mb-2">
                  Welcome Video Coming Soon
                </p>
                <p className="text-white/60 text-center text-sm max-w-md">
                  Aanchal Ma'am will personally guide you through what to expect and how to prepare for your trial class.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* What Happens Next Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8">
          <h3 className="text-xl font-bold text-slate-800 mb-6 text-center">
            What Happens Next?
          </h3>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Step 1 */}
            <div className="relative">
              <div className="flex flex-col items-center text-center">
                <div className="w-14 h-14 bg-rose-100 rounded-full flex items-center justify-center mb-4 relative">
                  <Phone className="w-7 h-7 text-rose-500" />
                  <span className="absolute -top-1 -right-1 w-6 h-6 bg-rose-500 text-white rounded-full text-xs flex items-center justify-center font-bold">1</span>
                </div>
                <h4 className="font-semibold text-slate-800 mb-2">We'll Call You</h4>
                <p className="text-sm text-slate-600">
                  Our team will call you within <strong>48 hours</strong> (except Sundays) to understand your pregnancy stage and preferences.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative">
              <div className="flex flex-col items-center text-center">
                <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mb-4 relative">
                  <Calendar className="w-7 h-7 text-amber-600" />
                  <span className="absolute -top-1 -right-1 w-6 h-6 bg-amber-500 text-white rounded-full text-xs flex items-center justify-center font-bold">2</span>
                </div>
                <h4 className="font-semibold text-slate-800 mb-2">Schedule Your Trial</h4>
                <p className="text-sm text-slate-600">
                  Based on your trimester, we'll help you pick the <strong>perfect batch and time slot</strong>.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative">
              <div className="flex flex-col items-center text-center">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mb-4 relative">
                  <Heart className="w-7 h-7 text-green-600" />
                  <span className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 text-white rounded-full text-xs flex items-center justify-center font-bold">3</span>
                </div>
                <h4 className="font-semibold text-slate-800 mb-2">Attend Your Class</h4>
                <p className="text-sm text-slate-600">
                  Join your <strong>small group trial session</strong> via Zoom with our expert instructors!
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Important Info Box */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-8">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h4 className="font-semibold text-amber-800 mb-1">Keep Your Phone Handy!</h4>
              <p className="text-sm text-amber-700">
                Our team will reach out to you within 48 hours (except Sundays) to schedule your trial class. 
                If you have any questions, feel free to <a href="https://wa.me/919876543210?text=Hi!%20I%20just%20registered%20for%20a%20trial%20class." target="_blank" rel="noopener noreferrer" className="font-semibold text-amber-800 underline">WhatsApp us</a>.
              </p>
            </div>
          </div>
        </div>

        {/* About Our Classes Section */}
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5 mb-8">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h4 className="font-semibold text-indigo-800 mb-1">Small Group Batches</h4>
              <p className="text-sm text-indigo-700">
                Our classes are conducted in <strong>small group batches</strong>, carefully segregated by <strong>trimester and risk factors</strong>. 
                This ensures you practice safely with moms at a similar stage of pregnancy, with personalized attention from our instructors.
              </p>
            </div>
          </div>
        </div>

        {/* What to Expect Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8">
          <h3 className="text-xl font-bold text-slate-800 mb-6 text-center">
            What to Expect in Your Trial Class
          </h3>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
              <div className="w-8 h-8 bg-rose-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-rose-600 font-bold">1</span>
              </div>
              <div>
                <h5 className="font-medium text-slate-800">Welcome & Introduction</h5>
                <p className="text-sm text-slate-600">A brief welcome message to help you settle in with the group</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
              <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-amber-600 font-bold">2</span>
              </div>
              <div>
                <h5 className="font-medium text-slate-800">Gentle Warm-up</h5>
                <p className="text-sm text-slate-600">Easy stretches designed specifically for your trimester</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-green-600 font-bold">3</span>
              </div>
              <div>
                <h5 className="font-medium text-slate-800">Prenatal Yoga Session</h5>
                <p className="text-sm text-slate-600">45-minute guided session with trimester-specific poses</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
              <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-indigo-600 font-bold">4</span>
              </div>
              <div>
                <h5 className="font-medium text-slate-800">Breathing & Relaxation</h5>
                <p className="text-sm text-slate-600">Learn Lamaze breathing techniques for easier labor</p>
              </div>
            </div>
          </div>
          
          {/* Pro Tip - Camera ON mandatory */}
          <div className="mt-6 p-4 bg-rose-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Video className="w-5 h-5 text-rose-600" />
              <span className="font-semibold text-rose-700">Important:</span>
            </div>
            <p className="text-rose-700 text-sm">
              It's <strong>mandatory to keep your camera ON</strong> during the class as this is a live yoga session. 
              Our instructor needs to see you to correct your posture and ensure you're practicing safely. 
              Please wear comfortable clothes, have water nearby, and find a quiet space with enough room to stretch!
            </p>
          </div>
        </div>

        {/* Post-Trial: 1:1 Consultation Section */}
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-6 md:p-8 mb-8">
          <div className="text-center mb-4">
            <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <MessageCircle className="w-7 h-7 text-emerald-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-800">After Your Trial Class</h3>
          </div>
          <p className="text-slate-700 text-center max-w-2xl mx-auto">
            Post your trial class, you can book a <strong className="text-emerald-700">complimentary 1:1 consultation</strong> with our 
            <strong className="text-emerald-700"> Senior Prenatal Consultant</strong>. During this personalized session, they will 
            discuss your pregnancy journey, explain our complete program in detail, and answer all your questions. 
            If it feels right for you, you can then enroll for the full program.
          </p>
        </div>

        {/* Trust Section */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-6 md:p-8 text-white mb-8">
          <h3 className="text-xl font-bold mb-6 text-center">You're in Good Hands</h3>
          
          <div className="grid grid-cols-3 gap-4 text-center mb-6">
            <div>
              <div className="text-3xl md:text-4xl font-bold text-rose-400">7500+</div>
              <div className="text-sm text-slate-300">Happy Moms</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-amber-400">&lt;7 hrs</div>
              <div className="text-sm text-slate-300">Avg Labor</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-green-400">4.9★</div>
              <div className="text-sm text-slate-300">Google Rating</div>
            </div>
          </div>
          
          <div className="flex items-center justify-center gap-6 flex-wrap">
            <div className="flex items-center gap-2 text-sm">
              <Shield className="w-4 h-4 text-green-400" />
              <span>Doctor Recommended</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Star className="w-4 h-4 text-amber-400" />
              <span>Expert Instructors</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Users className="w-4 h-4 text-rose-400" />
              <span>Small Batch Sizes</span>
            </div>
          </div>
        </div>

        {/* Contact CTA */}
        <div className="text-center">
          <p className="text-slate-600 mb-4">
            Have questions? We're here to help!
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a 
              href="https://wa.me/919876543210?text=Hi!%20I%20just%20registered%20for%20a%20trial%20class." 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-green-500 text-white px-6 py-3 rounded-xl font-medium hover:bg-green-600 transition-colors"
              data-testid="whatsapp-btn"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              WhatsApp Us
            </a>
            <a 
              href="mailto:support@soulsanskar.com" 
              className="flex items-center gap-2 bg-slate-100 text-slate-700 px-6 py-3 rounded-xl font-medium hover:bg-slate-200 transition-colors"
            >
              Email Support
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-800 text-white py-8 mt-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <img 
            src={LOGO_URL} 
            alt="Soul Sanskar" 
            className="h-20 w-auto mx-auto mb-4 brightness-0 invert"
          />
          <p className="text-slate-400 text-sm">
            © 2026 Soul Sanskar Healthcare Private Limited. All rights reserved.
          </p>
          <p className="text-slate-500 text-xs mt-2">
            GSTIN: 29AALCB5733F1ZI | Unit 101, Oxford Towers, HAL Old Airport Rd, Bangalore
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TrialThankYouPage;
